import React from 'react'
import ReactMarkdown from 'react-markdown'
import Box from '@material-ui/core/Box'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Typography from '@material-ui/core/Typography'
import CalendarTodayOutlinedIcon from '@material-ui/icons/CalendarTodayOutlined'
import ConfirmationNumberOutlinedIcon from '@material-ui/icons/ConfirmationNumberOutlined'
import MessageOutlinedIcon from '@material-ui/icons/MessageOutlined'
import PlaceOutlinedIcon from '@material-ui/icons/PlaceOutlined'
import ScheduleIcon from '@material-ui/icons/Schedule'
import Dialog from '@material-ui/core/Dialog'
import Alert from '@material-ui/lab/Alert'
import { humanizeMinutes } from 'utils/dateUtils'
import { useStyles } from './styles'
import CloseOutlinedIcon from '@material-ui/icons/CloseOutlined'
import IconButton from '@material-ui/core/IconButton'
import LessonPackDetails from './lesson-packs-details'
import StreamSendMessageButton, {
  MaintenanceSendMessageButton,
} from 'components/stream-messaging/send-message-button'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'
import LockOutlinedIcon from '@material-ui/icons/LockOutlined'
import Button from '@material-ui/core/Button'
import { useAuth } from 'lib/auth'
import { useHistory } from 'react-router-dom'
import Grid from '@material-ui/core/Grid'
import { AUTH_ROOT } from 'env'
import PropTypes from 'prop-types'
import SensitiveInfoWrapper from 'lib/auth/SensitiveInfoWrapper'

const BookingDetails = ({
  lessonInfo,
  isOpen,
  setIsOpen,
  bookingUrl,
  coachName,
  coachExternalId,
  nextExpirationDate,
  lessonCreditBalanceQuantity,
  isSingleSession,
  setOpenRequestAccessCode,
  setOpenVerifyAccessCode,
  coachIsViewingOwnSchedule,
}) => {
  const {
    id,
    title,
    description,
    locationName,
    durationInMinutes,
    lessonPacks = [],
    requiresAccessCode,
  } = lessonInfo
  const classes = useStyles()
  const { isLoggedIn } = useAuth()
  const history = useHistory()
  const isMobile = useMediaQuery(useTheme().breakpoints.down('xs'))
  const [enabledMessagingService] = useFeatureFlags([
    flags.FLAG_MESSAGING_SERVICE,
  ])

  const formattedExpirationDate = expDate => {
    return format(parseISO(expDate), 'MMMM d, yyyy')
  }

  const handleRequestCodeClick = () => {
    setIsOpen(false)
    if (!isLoggedIn) {
      const authUrl = `${AUTH_ROOT}/login/?return_to=${encodeURIComponent(
        window.location.href + `?lessonTypeId=${id}`,
      )}`
      window.location.href = authUrl
      return
    }
    setOpenRequestAccessCode(true)
  }

  let redirectUrl =
    isSingleSession || coachIsViewingOwnSchedule
      ? bookingUrl
      : `${bookingUrl}&isLessonPacks=true`
  redirectUrl = lessonCreditBalanceQuantity
    ? `${redirectUrl}&lessonCreditBalanceQuantity=${lessonCreditBalanceQuantity}`
    : redirectUrl

  const handleBookClick = () => {
    if (requiresAccessCode) {
      setIsOpen(false)
      setOpenVerifyAccessCode(true)
      return
    }
    history.push(redirectUrl)
  }

  const sendMessageButtonProps = {
    color: 'primary',
    variant: 'outlined',
    children: 'Send Message',
    startIcon: <MessageOutlinedIcon />,
  }
  const SendMessageButton = (() => {
    switch (enabledMessagingService) {
      case 'stream':
        return () => (
          <SensitiveInfoWrapper>
            <StreamSendMessageButton
              userIds={[coachExternalId]}
              {...sendMessageButtonProps}
            />
          </SensitiveInfoWrapper>
        )
      case 'maintenance':
      default:
        return () => (
          <MaintenanceSendMessageButton {...sendMessageButtonProps} />
        )
    }
  })()

  return (
    <Dialog
      open={!!isOpen}
      onClose={() => setIsOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box className={classes.actionButtonContainer}>
          <IconButton onClick={() => setIsOpen(false)}>
            <CloseOutlinedIcon color="primary" />
          </IconButton>
        </Box>
        {title}
        {description ? (
          <Typography variant="body1" color="textSecondary">
            <ReactMarkdown>{description}</ReactMarkdown>
          </Typography>
        ) : null}
      </DialogTitle>

      <DialogContent className={classes.contentRootV2}>
        <Typography variant="subtitle1">Where</Typography>
        <Box display="flex" alignItems="center" className={classes.detailsRow}>
          <PlaceOutlinedIcon className={classes.rowIconPrimary} />
          <Box>
            <Typography variant="body1">{locationName}</Typography>
          </Box>
        </Box>

        <Typography variant="subtitle1">Time duration</Typography>
        <Box display="flex" alignItems="center" className={classes.detailsRow}>
          <ScheduleIcon className={classes.rowIconPrimary} />
          <Typography variant="body1">
            {humanizeMinutes(durationInMinutes)}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CalendarTodayOutlinedIcon />}
            className={classes.alignRight}
            onClick={handleBookClick}
          >
            Book {isMobile ? '' : 'Session'}
          </Button>
        </Box>

        {requiresAccessCode && !lessonCreditBalanceQuantity && (
          <>
            <Typography variant="subtitle1">Access code required</Typography>
            <Grid container className={classes.detailsRow}>
              <Grid item xs={7}>
                <Typography variant="body1" color="textSecondary">
                  This offering requires an access code provided by the coach.
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Button
                  variant="outlined"
                  startIcon={isMobile ? null : <LockOutlinedIcon />}
                  onClick={handleRequestCodeClick}
                  className={classes.alignRight}
                >
                  Request Code
                </Button>
              </Grid>
            </Grid>
          </>
        )}

        {lessonPacks.length > 0 && !coachIsViewingOwnSchedule && (
          <>
            <Typography variant="subtitle1">Lesson packs</Typography>
            {!isSingleSession ||
            (isSingleSession && !lessonCreditBalanceQuantity) ? (
              <Box width="100%" className={classes.detailsRow}>
                {lessonPacks.map(lessonPack => (
                  <LessonPackDetails
                    {...lessonPack}
                    isMobile={isMobile}
                    bookingUrl={bookingUrl}
                    key={lessonPack.id}
                  />
                ))}
              </Box>
            ) : (
              <Box width="100%" className={classes.detailsRow}>
                <Alert
                  severity="info"
                  className={classes.warningBox}
                  icon={
                    <ConfirmationNumberOutlinedIcon
                      color="primary"
                      fontSize="small"
                    />
                  }
                >
                  <strong>
                    You have {lessonCreditBalanceQuantity} lessons left.
                  </strong>{' '}
                  {nextExpirationDate
                    ? `They expire on ${formattedExpirationDate(
                      nextExpirationDate,
                    )}`
                    : ''}
                </Alert>
              </Box>
            )}
          </>
        )}

        <Typography variant="subtitle1" gutterBottom>
          Questions?
        </Typography>
        <Typography
          variant="body1"
          color="textSecondary"
          style={{ marginBottom: '16px' }}
        >
          If you have any additional questions, please message {coachName}.
        </Typography>
        <SendMessageButton />
      </DialogContent>
    </Dialog>
  )
}

BookingDetails.propTypes = {
  lessonInfo: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    locationName: PropTypes.string.isRequired,
    durationInMinutes: PropTypes.number.isRequired,
    requiresAccessCode: PropTypes.bool.isRequired,
    lessonPacks: PropTypes.object.isRequired,
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  bookingUrl: PropTypes.string.isRequired,
  coachName: PropTypes.string.isRequired,
  coachExternalId: PropTypes.string.isRequired,
  nextExpirationDate: PropTypes.string.isRequired,
  lessonCreditBalanceQuantity: PropTypes.number.isRequired,
  isSingleSession: PropTypes.bool.isRequired,
  setOpenRequestAccessCode: PropTypes.func.isRequired,
  setOpenVerifyAccessCode: PropTypes.func.isRequired,
  coachIsViewingOwnSchedule: PropTypes.bool.isRequired,
}

export default BookingDetails
